import { queryApiSlice } from '../apiSlice';

interface IGetVotersParams {
	FirstName?: string;
	LastName?: string;
	MiddleName?: string;
	Email?: string;
	VoterRegistrationNumber?: string;
	PhoneNumber?: string;
	PollingUnitCodes?: string[];
	ExcludedPollingUnitCodes?: string[];
	StateCodes?: string[];
	ExcludedStateCodes?: string[];
	LgaCodes?: string[];
	ExcludedLgaCodes?: string[];
	WardCodes?: string[];
	ExcludedWardCodes?: string[];
	PageNumber?: number;
	PageSize?: number;
	Sort?: string;
	SortBy?: string;
	OrderBy?: string;
	MinCreatedDate?: string;
	MaxCreatedDate?: string;
	MinUpdatedDate?: string;
	MaxUpdatedDate?: string;
	IsBoolFilterOn?: string;
	IsDateFilterOn?: string;
	TimeZone?: string;
	QuickDateFilter?: string;
}

interface IGetVotersResponse {
	message: string;
	isSuccessful: boolean;
	statusCode: number;
	data: string[];
	currentPage: number;
	lastPage: number;
	total: number;
	pageSize: number;
}

interface IVoterCountResponse {
	message: string;
	isSuccessful: boolean;
	statusCode: number;
	total: number;
}

// Whether api.query has `get-count` deployed. Reading a count off `get-all-paged` also runs the
// OFFSET/LIMIT query and the name-masking projection, so `get-count` is preferred — but the frontend
// and api.query roll separately, and the answer is the same either way. Latched at module scope so a
// pod without the endpoint costs ONE probe per session rather than one per call.
//
// `verdictPending` is what makes that "one" true. Probes fire concurrently (the audience count and a
// segment count land in the same tick), so a plain boolean let every caller in the first batch past
// the check before any of them had resolved it — two doomed requests instead of one. Callers that
// arrive while the verdict is unknown wait for it rather than racing to rediscover it.
//
// The verdict is THREE-valued on purpose, and only proof moves it off null:
//   true  — a request succeeded, so the endpoint exists
//   false — 404/405, so it definitively does not
//   null  — anything else (500, network error, 400): inconclusive, re-probe next call
// Treating "not 404/405" as proof of existence looked equivalent and was not: one transient 500 on
// the first count of a session latched `true`, after which every call hit the missing endpoint with
// no fallback and the reach panel stayed uncountable for the whole session. Inconclusive must stay
// inconclusive.
//
// Delete this fallback (and both flags) once every environment is on an image that has get-count.
let countEndpointAvailable: boolean | null = null;
let verdictPending: Promise<void> | null = null;

export const votersApiSlice = queryApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getVoters: builder.mutation<IGetVotersResponse, IGetVotersParams>({
			query: (body) => ({
				method: 'POST',
				url: '/i-elect/Voters/get-all-paged',
				body,
			}),
		}),

		// Counts the voters a filter matches, without paging any rows back.
		//
		// The filter MUST narrow the register: api.query answers an unfiltered body with 400 (and
		// before that guard existed, with the national total). Callers resolve their scope first and
		// skip the call entirely when it cannot be scoped — see positiveFilterOrNull.
		getVoterCount: builder.mutation<IVoterCountResponse, IGetVotersParams>({
			queryFn: async (body, _api, _extra, baseQuery) => {
				const countOnce = async () => {
					const counted = await baseQuery({ method: 'POST', url: '/i-elect/Voters/get-count', body });
					if (counted.error) return { error: counted.error };
					return { data: counted.data as IVoterCountResponse };
				};

				// Verdict unknown: the first caller establishes it, the rest wait for it.
				if (countEndpointAvailable === null) {
					if (verdictPending) {
						await verdictPending;
					} else {
						let settle!: () => void;
						verdictPending = new Promise<void>((resolve) => {
							settle = resolve;
						});
						const counted = await baseQuery({ method: 'POST', url: '/i-elect/Voters/get-count', body });
						const status = counted.error?.status;
						// Only proof moves the verdict. Anything other than a success or a definitive
						// 404/405 leaves it null so the next call probes again.
						if (!counted.error) countEndpointAvailable = true;
						else if (status === 404 || status === 405) countEndpointAvailable = false;
						verdictPending = null;
						settle();
						if (countEndpointAvailable === true) return { data: counted.data as IVoterCountResponse };
						// Inconclusive or absent — answer this call from the paged fallback below.
					}
				}

				if (countEndpointAvailable) return countOnce();

				const paged = await baseQuery({
					method: 'POST',
					url: '/i-elect/Voters/get-all-paged',
					body: { ...body, PageNumber: 1, PageSize: 1 },
				});
				if (paged.error) return { error: paged.error };
				const page = paged.data as IGetVotersResponse;
				return {
					data: {
						message: page.message,
						isSuccessful: page.isSuccessful,
						statusCode: page.statusCode,
						total: page.total,
					},
				};
			},
		}),
	}),
});

export const { useGetVotersMutation, useGetVoterCountMutation } = votersApiSlice;
